/** Which sign-ins reach this account, and how to add another.
 *
 * An account is not a provider: it is the thing several providers can open,
 * which is the whole point of listing them rather than showing one badge. The
 * row for a provider that is already linked names the login it came from; the
 * row for one that is not offers a link, and that link is a full navigation
 * to `/auth/<provider>/start?link=1` rather than a fetch, because the browser
 * has to leave for the provider and come back with a code.
 *
 * Two facts are read rather than stored. The first identity is the primary
 * one — `/api/me` orders them oldest first, and the oldest is the one the
 * account was created with — so nothing has to be written down to know which
 * it is. And a provider is only offered when `/api/providers` lists it: that
 * endpoint answers with the providers the admin has enabled *and* configured,
 * so a "Link" button that led to "not available on this server" cannot be
 * drawn. A provider the server does not offer is still shown, greyed, rather
 * than hidden, because "Discord is not on yet" is the answer to the question
 * the reader is asking.
 */

import { authStartUrl } from '@/lib/api';
import { ProviderMark } from '@/components/ProviderMark';
import type { AccountIdentity, SignInProvider } from '@/lib/types';

export interface IdentityRowsProps {
  identities: readonly AccountIdentity[];
  /** From `/api/providers`; null when that call failed, in which case every
   *  unlinked provider is offered and the server refuses if it must. */
  providers: readonly SignInProvider[] | null;
}

/** The order the design lists them in; anything else follows, alphabetically. */
const ORDER = ['github', 'discord', 'google'];

function rank(name: string): number {
  const at = ORDER.indexOf(name);
  return at === -1 ? ORDER.length : at;
}

function titled(name: string): string {
  return name.length > 0 ? name[0]!.toUpperCase() + name.slice(1) : name;
}

export function IdentityRows({ identities, providers }: IdentityRowsProps) {
  const primaryId = identities[0]?.id ?? null;

  const names = new Set<string>();
  for (const identity of identities) names.add(String(identity.provider));
  for (const provider of providers ?? []) names.add(provider.name);
  for (const name of ORDER) names.add(name);

  const rows = Array.from(names).sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {rows.map((name, index) => {
        const identity = identities.find((entry) => String(entry.provider) === name) ?? null;
        const offered = providers === null || providers.some((entry) => entry.name === name);
        const label = providers?.find((entry) => entry.name === name)?.label ?? titled(name);

        return (
          <div
            key={name}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '8px 0',
              fontSize: 13.5,
              // The design's fading rule, on every row but the last.
              background:
                index === rows.length - 1
                  ? undefined
                  : 'var(--row-rule) no-repeat bottom / 100% 1px',
            }}
          >
            <ProviderMark name={name} />
            <span>{label}</span>

            {identity ? (
              <span style={{ color: 'var(--color-neutral-500)', fontSize: 12 }}>
                {identity.login || 'linked'}
              </span>
            ) : (
              <span style={{ color: 'var(--color-neutral-500)', fontSize: 12 }}>not linked</span>
            )}

            {identity ? (
              identity.id === primaryId ? (
                <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--color-ok)' }}>
                  primary
                </span>
              ) : (
                <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--color-neutral-600)' }}>
                  linked
                </span>
              )
            ) : offered ? (
              <a
                className="btn btn-ghost"
                style={{ marginLeft: 'auto', fontSize: 12 }}
                href={authStartUrl(name, { link: true, redirectTo: '/profile' })}
              >
                Link
                <span className="sr-only">&nbsp;{label} to this account</span>
              </a>
            ) : (
              <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--color-neutral-600)' }}>
                not offered on this server
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

export default IdentityRows;
